/**
 * Globe-engine plumbing for the awareness runtime.
 *
 * The engine (getGlobeEngine) exists only while a globe mode is on screen, and
 * LoaderGlobe calls engine.clearAll() on every dataset change, which removes
 * every entity and bulk overlay the awareness layers drew. The runtime keeps
 * an "engine epoch" in the awareness store: it bumps when the engine appears
 * and after each dataset swap, and every engine-side layer effect re-applies
 * on it. Engine calls are deferred with queueMicrotask so they run after the
 * passive-effect flush of the same React commit (LoaderGlobe's clearAll and
 * visibility effects run in that flush, after this runtime's own effects).
 */
import type { GlobeEngine } from '../globe/GlobeEngine';
import { getGlobeEngine } from '../globe/engineHandle';
import { useAwareness } from './store';

export const ENGINE_POLL_MS = 1000;
/** Consecutive sightings before the engine counts as mounted (lets LoaderGlobe's ready effects run first). */
export const ENGINE_SETTLE_POLLS = 2;

/**
 * Run an engine call after the current effect flush. Skips silently when the
 * engine has gone; logs with context when the engine throws (the engine has
 * already reported the failure through its status channel in most cases).
 */
export function withEngine(label: string, job: (engine: GlobeEngine) => void): void {
  queueMicrotask(() => {
    const engine = getGlobeEngine();
    if (!engine) return;
    try {
      job(engine);
    } catch (err) {
      console.error(`[awareness] globe call "${label}" failed:`, err);
    }
  });
}

/** Same as withEngine for async engine methods (overlays). */
export function withEngineAsync(label: string, job: (engine: GlobeEngine) => Promise<void>): void {
  queueMicrotask(() => {
    const engine = getGlobeEngine();
    if (!engine) return;
    job(engine).catch((err: unknown) => {
      console.error(`[awareness] globe call "${label}" failed:`, err);
    });
  });
}

/**
 * Watch for the engine every second: engineMounted follows its presence (after
 * two consecutive sightings) and the epoch bumps each time it (re)appears.
 */
export function startEngineWatcher(): () => void {
  let sightings = 0;
  const check = () => {
    const store = useAwareness.getState();
    const present = getGlobeEngine() !== null;
    if (!present) {
      sightings = 0;
      if (store.engineMounted) store.setEngineMounted(false);
      return;
    }
    sightings++;
    if (sightings >= ENGINE_SETTLE_POLLS && !store.engineMounted) {
      store.setEngineMounted(true);
      store.bumpEngineEpoch();
    }
  };
  check();
  const id = setInterval(check, ENGINE_POLL_MS);
  return () => clearInterval(id);
}
